import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, TrendingUp, TrendingDown } from "lucide-react";
import { api, tl, dateTR, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EXPENSE_CATEGORIES = ["Arsa", "Ruhsat", "Beton", "Demir", "İşçilik", "Mimari Proje", "Elektrik", "Su / Tesisat", "Yalıtım", "Diğer"];
const INCOME_CATEGORIES = ["Daire Satışı", "Ön Ödeme", "Diğer Gelir"];

const Field = ({ label, children }) => (
  <label className="block"><div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">{label}</div>{children}</label>
);
const KCStyle = () => <style>{`.kc-input{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.12);padding:10px 12px;color:white;outline:none}.kc-input:focus{border-color:#d4af37}`}</style>;

const EntryDialog = ({ open, onOpenChange, kind, onSubmit, busy }) => {
  const cats = kind === "expense" ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;
  const [form, setForm] = useState({ category: cats[0], description: "", amount: 0, entry_date: "", note: "" });
  useEffect(() => { setForm({ category: cats[0], description: "", amount: 0, entry_date: "", note: "" }); }, [open, kind]);

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, amount: Number(form.amount) || 0 });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-[#d4af37]/20 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{kind === "expense" ? "Gider Ekle" : "Gelir Ekle"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Kategori">
              <select data-testid={`yapsat-${kind}-category`} value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} className="kc-input">
                {cats.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
              </select>
            </Field>
            <Field label="Tutar (₺) *"><input type="number" step="0.01" data-testid={`yapsat-${kind}-amount`} value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} className="kc-input" required /></Field>
            <Field label="Tarih"><input type="date" value={form.entry_date} onChange={(e)=>setForm({...form,entry_date:e.target.value})} className="kc-input" /></Field>
            <Field label="Açıklama"><input value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="kc-input" /></Field>
          </div>
          <Field label="Not"><textarea rows={2} value={form.note} onChange={(e)=>setForm({...form,note:e.target.value})} className="kc-input resize-none" /></Field>
          <button data-testid={`yapsat-${kind}-save`} disabled={busy} className="w-full bg-[#d4af37] text-[#121212] hover:bg-white py-3 font-display uppercase tracking-[0.2em] text-xs transition">{busy ? "Kaydediliyor..." : "Kaydet"}</button>
        </form>
        <KCStyle />
      </DialogContent>
    </Dialog>
  );
};

export default function YapSatDetail() {
  const { id } = useParams();
  const [y, setY] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openExp, setOpenExp] = useState(false);
  const [openInc, setOpenInc] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/yapsat/${id}`).then((r) => setY(r.data));
    api.get(`/yapsat/${id}/entries`).then((r) => setEntries(r.data || []));
    api.get(`/yapsat/${id}/summary`).then((r) => setSummary(r.data));
  };
  useEffect(() => { load(); }, [id]);

  const add = (kind) => async (payload) => {
    setBusy(true);
    try {
      await api.post(`/yapsat/${id}/${kind === "expense" ? "expenses" : "incomes"}`, payload);
      toast.success("Eklendi");
      if (kind === "expense") setOpenExp(false); else setOpenInc(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (eid) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/yapsat-entries/${eid}`); load(); };
  const filtered = entries.filter((e) => filter === "all" ? true : e.kind === filter);

  if (!y) return <div className="text-white/40 font-mono text-xs">Yükleniyor...</div>;

  return (
    <div data-testid="yapsat-detail-page">
      <Link to="/admin/yapsat" className="inline-flex items-center gap-2 text-white/55 hover:text-[#d4af37] transition font-mono text-[11px] uppercase tracking-[0.25em] mb-6"><ArrowLeft className="h-3.5 w-3.5" /> Geri</Link>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">Yap-Sat · {y.status}</p>
      <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl md:text-5xl">{y.name}</h1>
      {y.location && <p className="text-white/55 mt-2">{y.location}</p>}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-white/8 bg-[#121212] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Toplam Gelir</div>
          <div className="mt-2 font-display text-3xl text-emerald-400">{tl(summary?.total_income || 0)}</div>
        </div>
        <div className="border border-white/8 bg-[#121212] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Toplam Gider</div>
          <div className="mt-2 font-display text-3xl text-amber-300">{tl(summary?.total_expense || 0)}</div>
        </div>
        <div className="border border-[#d4af37]/40 bg-[#121212] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Net Kar / Zarar</div>
          <div className={`mt-2 font-display text-3xl ${(summary?.net||0) >= 0 ? "text-[#d4af37]" : "text-red-400"}`}>{tl(summary?.net || 0)}</div>
        </div>
      </div>

      {summary?.expense_by_category && Object.keys(summary.expense_by_category).length > 0 && (
        <div className="mt-6 border border-white/8 bg-[#0a0a0a] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-3">Kategori Bazlı Gider</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.expense_by_category).map(([k, v]) => (
              <div key={k} className="px-3 py-2 border border-[#d4af37]/30 text-xs">
                <span className="text-white/70">{k}</span>: <span className="text-[#d4af37] font-display">{tl(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em]">
          {["all","expense","income"].map((f) => (
            <button key={f} onClick={()=>setFilter(f)} data-testid={`filter-${f}`} className={`px-3 py-2 border ${filter===f ? "border-[#d4af37] text-[#d4af37]" : "border-white/10 text-white/55"} hover:text-[#d4af37] transition`}>
              {f === "all" ? "Tümü" : f === "expense" ? "Giderler" : "Gelirler"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={openExp} onOpenChange={setOpenExp}>
            <DialogTrigger asChild>
              <button data-testid="yapsat-add-expense" className="inline-flex items-center gap-2 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#121212] px-4 py-2.5 font-display uppercase tracking-[0.18em] text-xs transition">
                <TrendingDown className="h-4 w-4" /> Gider Ekle
              </button>
            </DialogTrigger>
            <EntryDialog open={openExp} onOpenChange={setOpenExp} kind="expense" onSubmit={add("expense")} busy={busy} />
          </Dialog>
          <Dialog open={openInc} onOpenChange={setOpenInc}>
            <DialogTrigger asChild>
              <button data-testid="yapsat-add-income" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#121212] hover:bg-white px-4 py-2.5 font-display uppercase tracking-[0.18em] text-xs transition">
                <TrendingUp className="h-4 w-4" /> Gelir Ekle
              </button>
            </DialogTrigger>
            <EntryDialog open={openInc} onOpenChange={setOpenInc} kind="income" onSubmit={add("income")} busy={busy} />
          </Dialog>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto border border-white/8">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-white/55 font-mono text-[10px] uppercase tracking-[0.2em]">
            <tr><th className="text-left p-3">Tip</th><th className="text-left p-3">Kategori</th><th className="text-left p-3">Açıklama</th><th className="text-left p-3">Tarih</th><th className="text-right p-3">Tutar</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center text-white/40">Kayıt yok</td></tr>
            ) : filtered.map((e) => (
              <tr key={e.id} className="border-t border-white/5 hover:bg-white/5 transition" data-testid={`yapsat-entry-${e.id}`}>
                <td className="p-3">
                  <span className={`text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 border ${e.kind==="expense" ? "border-amber-300/30 text-amber-300" : "border-emerald-400/30 text-emerald-400"}`}>
                    {e.kind === "expense" ? "Gider" : "Gelir"}
                  </span>
                </td>
                <td className="p-3 text-[#d4af37]">{e.category}</td>
                <td className="p-3 text-white/80">{e.description || "-"}</td>
                <td className="p-3">{dateTR(e.entry_date)}</td>
                <td className={`p-3 text-right font-display ${e.kind==="expense" ? "text-amber-300" : "text-emerald-400"}`}>{tl(e.amount)}</td>
                <td className="p-3 text-right"><button onClick={()=>remove(e.id)} className="text-white/40 hover:text-[#d4af37]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
